import * as React from 'react';
import { clsx } from 'clsx';

export interface SubSectionInteractiveProps {
  /**
   * The heading text (required)
   */
  title: string;
  /**
   * Content to display when expanded (list rows, etc.)
   */
  children?: React.ReactNode;
  /**
   * Whether the section is expanded (controlled mode)
   */
  expanded?: boolean;
  /**
   * Default expanded state (uncontrolled mode)
   * @default true
   */
  defaultExpanded?: boolean;
  /**
   * Callback when expanded state changes
   */
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

// Chevron icon - rotates based on expanded state
const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
      transition: 'transform 0.2s ease-in-out',
    }}
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="#2f2f2f"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Arbor Design System sub-section interactive styles
const subSectionInteractiveStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    width: '100%',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    boxSizing: 'border-box' as const,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    textAlign: 'left' as const,
    outline: 'none',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '12px 8px',
    borderRadius: '8px',
    transition: 'background-color 0.15s ease-in-out',
    minHeight: '16px',
    gap: '12px',
  },
  headerContentHover: {
    backgroundColor: '#f8f8f8',
  },
  headerContentFocus: {
    outline: '3px solid #3cad51',
    outlineOffset: '-3px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2f2f2f',
    lineHeight: '1.25',
    margin: 0,
    flex: 1,
    whiteSpace: 'nowrap' as const,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    flexShrink: 0,
  },
  content: {
    overflow: 'hidden',
    transition: 'max-height 0.3s ease-in-out, opacity 0.2s ease-in-out',
  },
  contentExpanded: {
    maxHeight: '2000px',
    opacity: 1,
  },
  contentCollapsed: {
    maxHeight: '0px',
    opacity: 0,
  },
  contentInner: {
    borderBottom: '1px solid #f8f8f8',
  },
};

/**
 * SubSectionInteractive component - Arbor Design System
 *
 * A collapsible sub-section component that can contain list rows and other content.
 * Click the header to expand/collapse the content area.
 *
 * @example
 * ```tsx
 * // Basic collapsible section
 * <SubSectionInteractive title="Personal Details">
 *   <ListRow label="Name" value="John Smith" />
 *   <ListRow label="Email" value="john@example.com" />
 * </SubSectionInteractive>
 *
 * // Initially collapsed
 * <SubSectionInteractive title="Additional Info" defaultExpanded={false}>
 *   <ListRow label="Notes" value="Some notes here" />
 * </SubSectionInteractive>
 *
 * // Controlled mode
 * <SubSectionInteractive
 *   title="Settings"
 *   expanded={isExpanded}
 *   onExpandedChange={setIsExpanded}
 * >
 *   <ListRow label="Theme" value="Dark" />
 * </SubSectionInteractive>
 * ```
 */
export const SubSectionInteractive = React.forwardRef<HTMLDivElement, SubSectionInteractiveProps>(
  (
    {
      title,
      children,
      expanded: controlledExpanded,
      defaultExpanded = true,
      onExpandedChange,
      className,
      style,
      ...props
    },
    ref
  ) => {
    // Support both controlled and uncontrolled modes
    const [internalExpanded, setInternalExpanded] = React.useState(defaultExpanded);
    const isControlled = controlledExpanded !== undefined;
    const isExpanded = isControlled ? controlledExpanded : internalExpanded;

    const [isHovered, setIsHovered] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);

    const handleToggle = () => {
      const newExpanded = !isExpanded;
      if (!isControlled) {
        setInternalExpanded(newExpanded);
      }
      onExpandedChange?.(newExpanded);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToggle();
      }
    };

    const headerContentStyle: React.CSSProperties = {
      ...subSectionInteractiveStyles.headerContent,
      ...(isHovered && subSectionInteractiveStyles.headerContentHover),
      ...(isFocused && subSectionInteractiveStyles.headerContentFocus),
    };

    const contentStyle: React.CSSProperties = {
      ...subSectionInteractiveStyles.content,
      ...(isExpanded
        ? subSectionInteractiveStyles.contentExpanded
        : subSectionInteractiveStyles.contentCollapsed),
    };

    return (
      <div
        ref={ref}
        className={clsx('arbor-sub-section-interactive', className)}
        style={{ ...subSectionInteractiveStyles.container, ...style }}
        {...props}
      >
        <button
          type="button"
          style={subSectionInteractiveStyles.header}
          onClick={handleToggle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          aria-expanded={isExpanded}
        >
          <div style={headerContentStyle}>
            <h3 style={subSectionInteractiveStyles.title}>{title}</h3>
            <span style={subSectionInteractiveStyles.iconWrapper}>
              <ChevronIcon expanded={isExpanded} />
            </span>
          </div>
        </button>
        <div style={contentStyle} aria-hidden={!isExpanded}>
          {children && (
            <div style={subSectionInteractiveStyles.contentInner}>{children}</div>
          )}
        </div>
      </div>
    );
  }
);

SubSectionInteractive.displayName = 'SubSectionInteractive';
